import { config } from '../config';
import type { AreaGeometry, GeocodeResult } from './types';

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return { 'X-Api-Key': config.apiKey, ...extra };
}

async function getJSON<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, headers: authHeaders() });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as T;
}

/** Центр карты и видимая область [minLon, minLat, maxLon, maxLat] - бэкенд
 *  ставит вперёд варианты рядом с ними. */
export interface SearchArea {
  readonly lat: number;
  readonly lon: number;
  readonly bbox?: readonly [number, number, number, number];
}

export interface AutocompleteResponse {
  readonly results: GeocodeResult[];
  /** id записи в search_log на бэкенде, нужен для /search/select */
  readonly logId: string | null;
}

export async function fetchAutocomplete(
  query: string,
  limit = 5,
  signal?: AbortSignal,
  near?: SearchArea | null,
): Promise<AutocompleteResponse> {
  const url = new URL('/autocomplete', config.apiBaseUrl);
  url.searchParams.set('q', query);
  url.searchParams.set('limit', String(limit));
  if (near) {
    url.searchParams.set('lat', String(near.lat));
    url.searchParams.set('lon', String(near.lon));
    if (near.bbox) url.searchParams.set('bbox', near.bbox.map(v => v.toFixed(5)).join(','));
  }

  const response = await fetch(url.toString(), { signal, headers: authHeaders() });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return {
    results: (await response.json()) as GeocodeResult[],
    logId: response.headers.get('X-Search-Log-Id'),
  };
}

/**
 * Сообщает бэкенду, какой вариант из выдачи /autocomplete пользователь
 * выбрал - implicit feedback для будущего обучения модели переранжирования.
 * Best-effort: неудача не должна мешать пользователю работать с картой.
 */
export function selectResult(logId: string, resultId: string): void {
  const url = new URL('/search/select', config.apiBaseUrl);
  fetch(url.toString(), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ logId, resultId }),
  }).catch((error: unknown) => console.error('Search select failed:', error));
}

/**
 * Сообщает бэкенду, что выбранный вариант - верный адрес, но у него нет
 * координат (не показывается на карте) - отдельный сигнал 'no_geo': это
 * проблема геопривязки данных, а не поиска или ранжирования.
 */
export function reportNoGeo(logId: string, resultId: string): void {
  const url = new URL('/search/select', config.apiBaseUrl);
  fetch(url.toString(), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ logId, resultId, feedbackType: 'no_geo' }),
  }).catch((error: unknown) => console.error('Search no-geo report failed:', error));
}

/**
 * Сообщает бэкенду, что ни один из вариантов /autocomplete не оказался
 * верным (в т.ч. когда выдача вообще пустая) - отдельный сигнал 'no_match':
 * это провал поиска, а не просто плохое ранжирование кандидатов.
 */
export function reportNoMatch(logId: string): void {
  const url = new URL('/search/select', config.apiBaseUrl);
  fetch(url.toString(), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ logId }),
  }).catch((error: unknown) => console.error('Search no-match report failed:', error));
}

/**
 * Сообщает бэкенду, что введённый текст вообще не является адресом
 * (например "вылет SU 5466 06.09 в 00:15") - отдельный сигнал 'not_address',
 * который не должен попадать в обучающую выборку ранжирования/поиска.
 */
export function reportNotAddress(logId: string): void {
  const url = new URL('/search/select', config.apiBaseUrl);
  fetch(url.toString(), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ logId, feedbackType: 'not_address' }),
  }).catch((error: unknown) => console.error('Search not-address report failed:', error));
}

export function fetchReverse(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<GeocodeResult | null> {
  const url = new URL('/reverse', config.apiBaseUrl);
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lon));

  return getJSON<GeocodeResult | null>(url.toString(), signal);
}

/**
 * Контур адресного объекта (регион, район, город, улица) по его ao_id.
 * null - геометрии в базе нет или это только точка.
 */
export function fetchGeometry(aoId: number, signal?: AbortSignal): Promise<AreaGeometry | null> {
  const url = new URL('/geometry', config.apiBaseUrl);
  url.searchParams.set('id', String(aoId));

  return getJSON<AreaGeometry | null>(url.toString(), signal);
}
